import ModelProxy from "../model-proxy"
import {EntityType} from "../../gd/const/item"
import ModelEntity from "../model-entity"
import DatetimeUtils from "../../gd/utils/datetime-utils"
import MeetingUsers from "./users/meeting-users";
import ActionItemsDeprecated from "../todo/deprecated/action-items-deprecated";
import {TodoListAccess} from "../todo/@type";
import Notes from "../notes/notes";
import MyWorkMeeting from "../my-work-properties/meeting";

const debug = false
// const debug = "Meeting"


export default class Meeting extends ModelEntity {

    // public id               : ID
    public projectId?       : ID
    public name             : string

    public momentCreated?   : Moment

    public _startMoment?    : Moment | null
    public _endMoment?      : Moment | null

    public users?           : MeetingUsers
    public todo?            : ActionItemsDeprecated
    public notes?           : Notes

    public recurrencyId?    : ID

    private _myWorkProps?   : MyWorkMeeting

    // The own side of a RelationsCollection for a MeetingView (which is not a
    // Meeting). NOT MeetingView.toMeeting(): that round-trips the SAVE payload,
    // re-parsing every note and action item on each open. The own side is never
    // rendered, so the same compact summary the backend sends is enough.
    static relationProjection(data: any): Meeting {
        return new Meeting(_.pick(data, ['id', 'name', 'projectId', 'startMoment', 'endMoment']))
    }

    constructor(data:any, useProxy = true) {
        super(data['id'], EntityType.MEETING)

        // this.id             = data.id
        this.name           = data.name
        this.projectId      = data.projectId
        this.recurrencyId   = data.recurrencyId

        this.momentCreated  = data.momentCreated ? DatetimeUtils.datetimeToMoment(data.momentCreated) : undefined

        this._startMoment   = data['startMoment'] ? DatetimeUtils.datetimeToMoment(data['startMoment']) : undefined
        this._endMoment     = data['endMoment'] ? DatetimeUtils.datetimeToMoment(data['endMoment']) : undefined

        if (data.users) {
            this.users = MeetingUsers.initFromJson(data.users)
        }


        if (data.todo) {
            const todoAccess: TodoListAccess    = {
                add     : true,
                edit    : this.isOrganizer
            }
            this.todo = new ActionItemsDeprecated(this, todoAccess, data.todo);
        }

        if (data.notes) {
            const notes : JSONArray = _.filter(data.notes, note=>note.access === gd.const.meeting.noteAccess.PUBLIC || note.userId === gd.session.me.id)
            this.notes = Notes.initFromJson(notes);
        }

        return useProxy ? new Proxy(this, ModelProxy) : this
    }

    get projectPath(): ID | null {
        return !!this.projectId ? this.projectId : null
    }

    get project() {
        return gd.session.projects.get(this.projectId)
    }

    get isRecurrent():boolean {
        return !!this.recurrencyId
    }


    get myWorkProps():MyWorkMeeting {
        if (!this._myWorkProps) {
            this._myWorkProps = new MyWorkMeeting(this)
        }

        return this._myWorkProps
    }

    get isOrganizer():boolean {
        if (!this.users) return false
        return this.users.isOrganizer(gd.user.id)
    }

    get durationMinutes(): number | undefined {
        return this._startMoment && this._endMoment ? this._endMoment.diff(this._startMoment, "minutes") : undefined
    }

    get startMoment (): Moment | undefined | null {
        return this._startMoment
    }

    set startMoment(newStartMoment:Moment|null|undefined) {
        const durationMinutes   = this.durationMinutes
        this._startMoment       = newStartMoment
        this._endMoment         = this._startMoment ? moment(this._startMoment).add(durationMinutes, 'minutes') : undefined
        this.updatedFields.add('endMoment')
    }

    get endMoment (): Moment| undefined | null {
        return this._endMoment
    }

    set endMoment(newEndMoment: Moment | null | undefined ) {
        if (newEndMoment && this._startMoment && newEndMoment.isBefore(this._startMoment, 'minutes')) {
            this._endMoment = moment(this._startMoment)
        } else {
            this._endMoment  = newEndMoment
            if (!newEndMoment) {
                this._startMoment = undefined
                this.updatedFields.add('startMoment')
            }
        }
    }

    get usersIds() {
        return this.users?.userIds
    }

    goTo() {
        gd.navigation.goToMeeting(this.id)
    }

    _isSameValue(paramName:string, value:any) {
        // @ts-ignore
        const currentValue: any = this[paramName]

        if (moment.isMoment(currentValue)) {
            // console.log("isSameValue", DatetimeUtils.momentToDate(this[paramName]), DatetimeUtils.dateTruncTime(value), DatetimeUtils.isSame(this[paramName], DatetimeUtils.dateToMoment(value), 'day'))
            return DatetimeUtils.isSame(currentValue, DatetimeUtils.dateToMoment(value), 'day')
        } else {
            return _.isEqual(currentValue,value)
        }
    }

    applyDelta(data:any) {
        debug && console.log(debug, "applyDelta()", data)

        let isChanged = false

        _.mapKeys(data, (value ,key)=>{
            if (key === 'id') return
            // @ts-ignore
            debug && console.log(debug, "applyDelta key=", key, !this._isSameValue(key, value), this[key], value)

            if (!this._isSameValue(key, value)) {
                debug && console.log(".....set", key, "neValue", value)

                switch (key) {
                    case 'startMoment'  : this['_startMoment'] = DatetimeUtils.datetimeToMoment(value); break;
                    case 'endMoment'    : this['_endMoment'] = DatetimeUtils.datetimeToMoment(value); break;

                    case 'users':
                        this.users = MeetingUsers.initFromJson(value)
                        break

                    case 'todo':
                        const todoAccess: TodoListAccess    = {
                            add     : true,
                            edit    : this.isOrganizer
                        }
                        this.todo = new ActionItemsDeprecated(this, todoAccess, value);
                        break

                    default             :
                        // @ts-ignore
                        this[key] = value
                }

                isChanged = true
            }
        })

        debug && console.log(debug, "....applyDelta isChanged", isChanged)

        return isChanged
    }



    // Management

    async delete() {
        return await gd.api.Meeting.meetingDelete(this.id).promise()
    }

    async saveUniversal() {

        debug && console.log(debug, "saveUniversal", [...this.updatedFields])

        const params = {}

        // this is for universal update
        this.updatedFields.forEach((fieldName) => {

            switch (fieldName) {

                case 'startMoment':
                    // @ts-ignore
                    params['startMoment'] = DatetimeUtils.momentToUTCISODatetime(this[fieldName])
                    break

                case 'endMoment':
                    // @ts-ignore
                    params['endMoment'] = DatetimeUtils.momentToUTCISODatetime(this[fieldName])
                    break

                default:
                    // @ts-ignore
                    params[fieldName] = this[fieldName]
                    break
            }
        })

        this.updatedFields.clear()

        debug && console.log(debug, "saveUniversal", this.id, params)
        if (params && _.size(params)>0) {
            return await gd.api.Meeting.meetingUpdate(this.id, params).promise()
        }

        return true
    }

    update(newData: any) {

        // console.log("SessionMeetingModel.update", newData);

        if ('name' in newData) this.name = newData['name'];
        if ('projectId' in newData) this.projectId = newData['projectId'];
        if ('startMoment' in newData) this._startMoment = DatetimeUtils.datetimeToMoment(newData['startMoment']);
        if ('endMoment' in newData) this._endMoment = DatetimeUtils.datetimeToMoment(newData['endMoment']);

    }

    // EXPORT

    toJSON() {
        return {
            id          : this.id,
            name        : this.name,
            projectId   : this.projectId,
            startMoment : this._startMoment ? DatetimeUtils.momentToUTCISODatetime(this._startMoment) : undefined,
            endMoment   : this._endMoment ? DatetimeUtils.momentToUTCISODatetime(this._endMoment) : undefined
        }
    }
}



